import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { fetchAssignedWorkoutTree, type AssignedWorkoutTree } from "../lib/assignedWorkoutTree";
import { workoutOverview, type WorkoutPreview } from "../lib/workoutPreview";
import { EquipmentIcon } from "./EquipmentIcon";
import { PhonePlayer, newPlayerView, type PlayerView } from "./PhonePlayer";
import { railCurve, walk as makeWalk, walkModel } from "../lib/playerWalk";
import { RAIL, WORDS } from "../lib/playerFacts";
import { PHONE_POINTS, equipmentChips, exerciseIdsOf } from "../lib/phoneOverview";
import { noteTakesFocus, restLine } from "../lib/weekView";
import type { ExerciseLabel } from "../lib/exerciseLabels";
import { saidHead, type Said } from "../lib/carryFeedback";
import {
  BRIEFING, CARD_LEADING, DIVIDER_LEADING, SPACING, SPINE_X,
  briefingBlocks, monogramInitial, phoneThumbnail, type BriefingBlock, type Connector,
} from "../lib/briefing";

/* W66 — the client's workout overview, drawn in an iPhone Pro frame and PORTED FROM ITS SOURCE
 * (`PreWorkoutBriefingView.swift`; every number is `lib/briefing.ts`'s, each ruled against the
 * Swift). Sizes inside the screen are PHONE POINTS through `--pt` (one point = 1/402 of the
 * screen's width). His 2026-09-24 ~22:40 word: no back, no settings, no Start — so the content
 * moves up to the top inset. */
export type Media = { demo: string | null; thumb: string | null };

export const BRIEFING_VARS = {
  "--b-xxs": SPACING.xxs, "--b-xs": SPACING.xs, "--b-sm": SPACING.sm, "--b-md": SPACING.md,
  "--b-card-lead": CARD_LEADING, "--b-divider-lead": DIVIDER_LEADING,
  "--b-thumb": BRIEFING.thumbSize, "--b-thumb-gap": BRIEFING.thumbGap, "--b-thumb-radius": BRIEFING.thumbRadius,
  "--b-row-min": BRIEFING.rowMinHeight, "--b-card-radius": BRIEFING.cardRadius,
  "--b-header-min": BRIEFING.headerMinHeight, "--b-title": BRIEFING.titleSize,
  "--b-mono": BRIEFING.thumbSize * BRIEFING.monogramScale, "--b-icon": BRIEFING.equipmentIcon,
} as React.CSSProperties;

/* W95 — `tree` draws a tree the caller already holds (the builder's LIVE draft, `draftTree`); otherwise `workoutId` is read
   from the database (the assign window). One reader either way. */
export function PhoneOverview({ workoutId, tree, startable = false, onPlayingChange }: {
  workoutId?: string;
  tree?: AssignedWorkoutTree;
  /** 🔴 W130 — the DEVICE FRAME's start capsule (the builder's phone window and the Assign window's preview). Never set by
   *  the Week's Extended column, which draws `OverviewBody` with no frame. */
  startable?: boolean;
  /** W130 — told when the walk-through opens and closes, so the frame's caller can say `Preview — nothing is saved`. */
  onPlayingChange?: (playing: boolean) => void;
}) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; preview: WorkoutPreview; chips: { key: string; label: string }[]; media: Map<string, Media>;
        tree: AssignedWorkoutTree; fields: Map<string, readonly string[]> }
  >({ kind: "loading" });
  /* W130 — the walk: held here while the frame is open (back keeps it; Start again continues it), dropped when the frame
     unmounts (the dialog closing). Nothing in it is ever written anywhere. */
  const [playing, setPlaying] = useState(false);
  const [view, setView] = useState<PlayerView | null>(null);
  const walk = useMemo(() => (state.kind === "ready" ? makeWalk(walkModel(state.tree, state.fields)) : null), [state]);
  useEffect(() => { setPlaying(false); setView(null); }, [walk]);
  useEffect(() => { onPlayingChange?.(playing); }, [playing, onPlayingChange]);
  /* Back from the walk returns focus to the capsule that opened it, never to the page behind the dialog. */
  const startRef = useRef<HTMLButtonElement>(null);
  const wasPlaying = useRef(false);
  useEffect(() => {
    if (wasPlaying.current && !playing) startRef.current?.focus();
    wasPlaying.current = playing;
  }, [playing]);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      const r = tree !== undefined ? { tree, error: "" }
        : workoutId === undefined ? { tree: null, error: "Nothing to show yet." }
        : await fetchAssignedWorkoutTree(
          supabase.rpc.bind(supabase) as Parameters<typeof fetchAssignedWorkoutTree>[0], workoutId);
      if (cancelled) return;
      if (r.tree === null) { setState({ kind: "error", message: r.error }); return; }
      const ids = exerciseIdsOf(r.tree);
      const byExercise = new Map<string, readonly string[]>();
      const media = new Map<string, Media>();
      const fields = new Map<string, readonly string[]>();
      if (ids.length > 0) {
        // The tree carries names, not equipment or media; both are the LIBRARY row's own answer,
        // which is also what the phone's media cache is keyed on (`exercise.catalogId`).
        const { data } = await supabase.from("coach_exercises")
          .select("id, equipment, demo_video_url, thumbnail_url, tracking_fields").in("id", ids);
        for (const row of (data ?? []) as { id: string; equipment: string[] | null; demo_video_url: string | null; thumbnail_url: string | null; tracking_fields: string[] | null }[]) {
          byExercise.set(row.id, row.equipment ?? []);
          // W130 — the walk's set table reads the coach's `tracking_fields` (its columns and their order), one column more on
          // the same read.
          fields.set(row.id, row.tracking_fields ?? []);
          media.set(row.id, { demo: row.demo_video_url, thumb: row.thumbnail_url });
        }
      }
      if (cancelled) return;
      setState({ kind: "ready", preview: workoutOverview(r.tree, fields), chips: equipmentChips(r.tree, byExercise), media, tree: r.tree, fields });
    })();
    return () => { cancelled = true; };
  }, [workoutId, tree]);

  return (
    <div className="phone-frame" style={{ aspectRatio: `${PHONE_POINTS.width} / ${PHONE_POINTS.height}` }}
         /* W130 — a frame that can be walked holds real buttons, so it is a GROUP (an `img` makes its children
            presentational and would hide them from assistive tech); a frame only to look at stays an image. */
         role={startable ? "group" : "img"} aria-label="How this workout looks on the client's phone">
      {/* The phone's numbers reach the stylesheet as variables from `lib/briefing.ts`, so the CSS
          never states a size of its own that could drift from the Swift the ruler reads. */}
      <div className={`phone-screen${playing ? " is-playing" : ""}`} style={BRIEFING_VARS}>
        {state.kind === "loading" && <p className="phone-note">Loading…</p>}
        {state.kind === "error" && <p className="phone-note" role="alert">{state.message}</p>}
        {state.kind === "ready" && walk !== null && playing && view !== null ? (
          <PhonePlayer walk={walk} view={view} onView={setView} media={state.media}
                       onBack={() => { setPlaying(false); }} />
        ) : state.kind === "ready" && (
          <>
            <OverviewBody preview={state.preview} chips={state.chips} media={state.media} />
            {/* 🔴 W130 — the phone's own floating capsule: its start word, then its continue word once the walk has begun
                (`PreWorkoutBriefingView`'s two words). The ONE primary inside the device; the page's own primary is
                untouched. Shown only where there is something to walk. */}
            {startable && walk !== null && walk.pages.length > 0 && (
              <button type="button" className="phone-start" ref={startRef}
                      onClick={() => { setView((v) => v ?? newPlayerView(walk)); setPlaying(true); }}>
                {view === null ? WORDS.start : WORDS.continue}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 🔴 W137 — THE OVERVIEW'S CONTENT, ONE DEFINITION FOR TWO FRAMES (Fable R3). The device frame above draws it inside the
 * phone; the client Week's EXTENDED column draws it with no phone around it (his 2026-10-02 word: *"like the phone workout
 * overview of the client, without the phone, just the container"*). Never a second composition: the title, the description,
 * the equipment row, the cards with their band and rail, `+N more` — all of it is here, once.
 *
 * `head` replaces the phone's 34-pt title with the column's own title line (its actions live there). `extended` adds what
 * the coach needs and the phone's own row never prints: each exercise's REST and the coach's NOTE (his second message).
 */
/** Week editor stage 1 (2026-10-19) — the extended column's rows made editable: `lead` is the row's label slot (the label as
 *  the drag grip, the `⋯` under it — no width taken from a narrow column), `body` replaces the drawn dose · rest · note with
 *  the one-line field (handed what is drawn, for the at-rest look). Absent = the column only reads. */
export type RowEdit = {
  /** The whole row, wrapped — the drag handle (Ioannis 10-19: "can't we just drag exercises?"). */
  wrap: (rowId: string, row: React.ReactNode) => React.ReactNode;
  lead: (rowId: string, label: React.ReactNode) => React.ReactNode;
  body: (rowId: string, drawn: React.ReactNode) => React.ReactNode;
};

export function OverviewBody({ preview, chips, media, head, extended = false, notes = true, said, workoutSaid, labels, onPickRow, rowEdit }: {
  /** CARRY-FEEDBACK B — in the extended column: the coach's last review of this workout, drawn under the description. */
  workoutSaid?: Said;
  /** CARRY-FEEDBACK A — in the extended column: row id → the coach's last comment the client will meet there. */
  said?: ReadonlyMap<string, Said>;
  preview: WorkoutPreview;
  chips: readonly { key: string; label: string }[];
  media: ReadonlyMap<string, Media>;
  head?: React.ReactNode;
  extended?: boolean;
  /** In the extended column: the coach's exercise notes drawn (default) or hidden (his 2026-10-15 Show · Hide switch). */
  notes?: boolean;
  /** W137 R3b — the column's `A1 · B1 · W1 · C1` labels (`exerciseLabels`), drawn before each name. */
  labels?: ReadonlyMap<string, ExerciseLabel>;
  /** W137 R3b — the column's exercise name is a button opening that exercise (*"Only if we click the exercise text it can
   *  open to see it"*). Absent = the name is text. */
  onPickRow?: (exerciseRowId: string) => void;
  /** Week editor stage 1 — the client Week's rows edited in place (`extended` only). */
  rowEdit?: RowEdit;
}) {
  const blocks = useMemo(() => briefingBlocks(preview), [preview]);
  return (
    <>
      {head ?? <h3 className="phone-title">{preview.name}</h3>}
      {/* The DESCRIPTION heading and its text. The (i) beside it is the phone's for an
          ENGINE-authored session only (`vm.isEngineAuthored`) — never on a coach's workout. */}
      {preview.description !== null && preview.description.trim() !== "" && (
        <>
          <p className="phone-overline phone-desc-head">Description</p>
          <p className="phone-desc">{preview.description}</p>
        </>
      )}
      {/* CARRY-FEEDBACK B — what the client will be SHOWN on this workout's page: the coach's last review of it. */}
      {extended && workoutSaid !== undefined && <p className="phone-desc phone-desc-said"><SaidLine said={workoutSaid} /></p>}
      {chips.length > 0 && (
        <div className="phone-equipment">
          <p className="phone-overline phone-equipment-head">Equipment</p>
          {/* ONE ROW THAT MOVES SIDEWAYS — his 2026-09-03 rule for the phone's chips and his
              2026-09-24 word for this preview: a swipe on touch, a drag with the mouse. */}
          <DragScroll className="phone-chips-scroll">
            <ul className="phone-chips">
              {chips.map((c) => (
                <li key={c.key} className="phone-chip"><EquipmentIcon equipmentKey={c.key} size={BRIEFING.equipmentIcon} />{c.label}</li>
              ))}
            </ul>
          </DragScroll>
        </div>
      )}
      <BriefingList blocks={blocks} media={media} extended={extended} notes={notes} said={said} labels={labels} onPickRow={onPickRow}
                    rowEdit={extended ? rowEdit : undefined} />
      {preview.more > 0 && <p className="phone-note">+{preview.more} more section{preview.more === 1 ? "" : "s"}</p>}
    </>
  );
}

/** CARRY-FEEDBACK — `YOU SAID · 15/10` and the words: the coach's last feedback, where the client will meet it. A left rule in
 *  the accent sets it apart from the coach's standing note (plain secondary text); clamped like the note, whole on hover/focus. */
export function SaidLine({ said }: { said: Said }) {
  return (
    <span className="phone-row-said" tabIndex={noteTakesFocus(said.text) ? 0 : undefined}>
      <span className="phone-row-said-head">{saidHead(said.dayKey)}</span>
      <span className="phone-row-said-text">{said.text}</span>
    </span>
  );
}

/** `BriefingExerciseList` + `SpineOverlay`: the cards, and the rail drawn from each thumbnail's
 *  measured centre — the phone's `anchorPreference(.center)` — never from a guessed offset. */
function BriefingList({ blocks, media, extended, notes, said, labels, onPickRow, rowEdit }: {
  blocks: BriefingBlock[]; media: ReadonlyMap<string, Media>; extended: boolean; notes: boolean;
  said?: ReadonlyMap<string, Said>;
  labels?: ReadonlyMap<string, ExerciseLabel>; onPickRow?: (exerciseRowId: string) => void; rowEdit?: RowEdit;
}) {
  const list = useRef<HTMLDivElement>(null);
  const [dots, setDots] = useState<{ y: number; connector: Connector }[]>([]);
  const [pt, setPt] = useState(1);

  useLayoutEffect(() => {
    const el = list.current;
    if (el === null) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top;
      const next = [...el.querySelectorAll<HTMLElement>("[data-spine]")].map((t) => {
        const r = t.getBoundingClientRect();
        return { y: r.top - top + r.height / 2, connector: t.dataset.spine as Connector };
      });
      setDots(next);
      /* W137 — one point is read off a drawn THUMBNAIL (56 pt wide on the phone), so the rail matches the cards in any frame:
         the phone's (where it equals the screen's width over 402) and the Week's narrow column (whose `--pt` is its own). */
      const thumb = el.querySelector<HTMLElement>(".phone-thumb");
      const tw = thumb === null ? 0 : thumb.getBoundingClientRect().width;
      setPt(tw > 0 ? tw / BRIEFING.thumbSize : el.clientWidth / PHONE_POINTS.width);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [blocks]);

  return (
    <div ref={list} className="phone-list">
      {/* 🔴 W137 R3b — the extended column draws NO rail (line, dots, superset arcs) and NO thumbnails, at any width: *"to
          save space don't use the line and dots left at all and no video containers"*. The label is the superset's mark. */}
      {!extended && <Spine dots={dots} pt={pt} />}
      {blocks.map((b) => (
        <section key={b.key} className="phone-card">
          <header className="phone-band">
            <span className="phone-band-title">{b.header === null ? "MAIN" : b.header.title}</span>
            {b.header?.figure != null && (
              <span className="phone-band-figure">
                <FigureGlyph symbol={b.header.figure.symbol} />{b.header.figure.text}
              </span>
            )}
            {b.header?.hasNote === true && <span className="phone-band-note" aria-hidden="true"><NoteGlyph /></span>}
          </header>
          {b.rows.map((r) => {
            const m = r.exerciseId === null ? undefined : media.get(r.exerciseId);
            const src = m === undefined ? null : phoneThumbnail(m.demo, m.thumb);
            const initial = monogramInitial(r.name);
            const label = labels?.get(r.id);
            const rowNode = (
                <div className="phone-row">
                  {!extended && (
                  <span className="phone-thumb" data-spine={r.connector}>
                    {src !== null ? (
                      <><img src={src} alt="" loading="lazy" /><PlayGlyph /></>
                    ) : (
                      <span className="phone-monogram">{initial ?? <FigureStrength />}</span>
                    )}
                  </span>
                  )}
                  {extended && rowEdit === undefined && label !== undefined && (
                    <span className={`phone-row-label is-${label.kind}`} aria-hidden="true">{label.label}</span>
                  )}
                  {extended && rowEdit !== undefined && rowEdit.lead(r.id, label === undefined ? null
                    : <span className={`phone-row-label is-${label.kind}`} aria-hidden="true">{label.label}</span>)}
                  <span className="phone-row-text">
                    {extended && onPickRow !== undefined ? (
                      <button type="button" className="phone-row-name phone-row-open"
                              aria-label={label === undefined ? r.name : `${label.label}, ${r.name}`}
                              onClick={() => { onPickRow(r.id); }}>
                        {r.name}
                      </button>
                    ) : (
                      <span className="phone-row-name">
                        {extended && label !== undefined && <span className="ht-visually-hidden">{label.label}, </span>}
                        {r.name}
                      </span>
                    )}
                    {rowEdit === undefined ? (
                      <>
                        <span className="phone-row-dose">{r.dose}</span>
                        {/* W137 — the extended column only: the rest (`Rest 1:30`, the builder's own words) and the coach's
                            note, clamped to three lines and opened on hover or focus. The phone's row prints neither. */}
                        {extended && restLine(r.rest) !== null && <span className="phone-row-rest">{restLine(r.rest)}</span>}
                        {extended && notes && r.note !== null && (
                          <span className="phone-row-note" tabIndex={noteTakesFocus(r.note) ? 0 : undefined}>{r.note}</span>
                        )}
                      </>
                    ) : rowEdit.body(r.id, (
                      /* Inside the line's button: the same three pieces, the note without a tab stop of its own. */
                      <>
                        <span className="phone-row-dose">{r.dose}</span>
                        {restLine(r.rest) !== null && <span className="phone-row-rest">{restLine(r.rest)}</span>}
                        {notes && r.note !== null && <span className="phone-row-note">{r.note}</span>}
                      </>
                    ))}
                    {/* CARRY-FEEDBACK A — what the client will be SHOWN here: the coach's last comment on this exercise (the
                        phone's `COACH SAID` card), on its first coming row only. Not a note: the notes switch leaves it. */}
                    {extended && said?.get(r.id) !== undefined && <SaidLine said={said.get(r.id)!} />}
                  </span>
                </div>
            );
            return (
              <div key={r.id} data-row-id={rowEdit === undefined ? undefined : r.id}>
                {rowEdit === undefined ? rowNode : rowEdit.wrap(r.id, rowNode)}
                {r.dividerAfter && <div className="phone-divider" />}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

/** `SpineOverlay`, point for point: a dashed rail from the first dot to the last, a dot at each
 *  thumbnail's centre, and a superset's loop — forward down the right, back up the left. */
function Spine({ dots, pt }: { dots: { y: number; connector: Connector }[]; pt: number }) {
  const first = dots[0], last = dots[dots.length - 1];
  if (first === undefined || last === undefined) return null;
  const x = SPINE_X * pt, r = BRIEFING.dotR * pt;
  const loops: string[] = [], heads: string[] = [];
  /* 🔴 THE PLAYER'S CURVES, TURNED VERTICAL (his word 2026-10-07: *"the arrows could look more like the horizontal view ones
     that's inside the workout … exactly like"*) — `railCurve`, the rail's own twin (proven against the Swift by the walk
     corpus), in points along the line (y) and across it (x: + right for the forward curve, − left for the back one). */
  const P = (side: 1 | -1, p: readonly [number, number]) => `${x + side * p[1] * pt},${p[0] * pt}`;
  const draw = (cv: ReturnType<typeof railCurve>) => {
    loops.push(`M${P(cv.side, cv.start)} Q${P(cv.side, cv.control)} ${P(cv.side, cv.strokeEnd)}`);
    heads.push(`M${P(cv.side, cv.tip)} L${P(cv.side, cv.left)} L${P(cv.side, cv.right)} Z`);
  };
  let start: number | null = null;
  dots.forEach((d, i) => {
    if (d.connector === "supersetFirst") start = i;
    if (d.connector === "supersetLast" && start !== null) {
      const top = dots[start]!.y / pt, bottom = d.y / pt;
      draw(railCurve(top, bottom, RAIL.curveForward, 1));
      draw(railCurve(bottom, top, RAIL.curveBack, -1));
      start = null;
    }
  });
  const w = BRIEFING.spineWidth * pt;
  return (
    <svg className="phone-spine" aria-hidden="true">
      <line x1={x} y1={first.y} x2={x} y2={last.y} strokeWidth={w}
            strokeDasharray={`${BRIEFING.dash * pt} ${BRIEFING.dash * pt}`} />
      {loops.map((d, i) => <path key={i} d={d} fill="none" strokeWidth={w} strokeLinecap="round" />)}
      {heads.map((d, i) => <path key={`h${i}`} d={d} className="phone-spine-fill" />)}
      {dots.map((d, i) => <circle key={i} cx={x} cy={d.y} r={r} className="phone-spine-fill" />)}
    </svg>
  );
}

/* SF Symbols the phone draws, as inline marks: `clock`, `arrow.trianglehead.2.clockwise`,
   `doc.text`, `play.circle.fill`, `figure.strengthtraining.functional`. */
function FigureGlyph({ symbol }: { symbol: "clock" | "cycle" }) {
  return symbol === "clock" ? (
    <svg viewBox="0 0 12 12" aria-hidden="true"><circle cx="6" cy="6" r="4.8" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M6 3.4V6l1.8 1.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
  ) : (
    <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.2 6a3.8 3.8 0 0 1 6.5-2.7M9.8 6a3.8 3.8 0 0 1-6.5 2.7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><path d="M8.2 1.4l.8 2.1-2.2.3M3.8 10.6 3 8.5l2.2-.3" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
  );
}
function NoteGlyph() {
  return <svg viewBox="0 0 20 20"><path d="M5 2.5h6.5L15 6v11.5H5z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><path d="M11.5 2.5V6H15M7.5 10h5M7.5 13h5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>;
}
function PlayGlyph() {
  return <svg className="phone-play" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9.5" className="phone-play-disc" /><path d="M8 6.2v7.6L14 10z" className="phone-play-arrow" /></svg>;
}
function FigureStrength() {
  return <svg className="phone-figure" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="4.5" r="2" fill="none" stroke="currentColor" strokeWidth="1.2" /><path d="M3 9h18M5 7v4M19 7v4M12 7v6l-3 7M12 13l3 7" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>;
}

/** A horizontal scroller a mouse can drag, the way a finger swipes it on the phone. Touch and the
 *  wheel scroll natively; the drag is the desktop's swipe. */
function DragScroll({ className, children }: { className: string; children: React.ReactNode }) {
  const el = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; left: number } | null>(null);
  return (
    <div ref={el} className={className}
         onPointerDown={(e) => {
           if (e.pointerType !== "mouse" || el.current === null) return;
           drag.current = { x: e.clientX, left: el.current.scrollLeft };
           el.current.setPointerCapture(e.pointerId);
         }}
         onPointerMove={(e) => {
           if (drag.current === null || el.current === null) return;
           el.current.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
         }}
         onPointerUp={() => { drag.current = null; }}
         onPointerCancel={() => { drag.current = null; }}>
      {children}
    </div>
  );
}
